import { useState } from 'react';
import { notification } from 'antd';
import { utils, writeFile } from 'xlsx';
import { useStoreState } from 'hooks';
import { useLogin } from 'store/auth';
import { flattenJSON } from 'utils/flatten';
import {
  ConsistencyRuleRunResult,
  RuleOperatorLabel,
} from '../consistencyReportTypes';
import { ConsistencyReportDetail } from '../hooks/useConsistencyReport';
import { fetchConsistencyFailedInstances } from '../hooks/fetchConsistencyFailedInstances';
import { calculateSuccessRate } from '../helpers';

const BATCH_SIZE = 500_000;

export const useDownloadConsistencyReport = (
  report: ConsistencyReportDetail | undefined
) => {
  const token = useStoreState((state) => state.auth.token);
  const tenant = useStoreState((state) => state.auth.tenant);
  const cluster = useStoreState((state) => state.auth.cluster);
  const expiry = useStoreState((state) => state.auth.expiry);
  const login = useLogin();
  const [downloading, setDownloading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const downloadTooltip = (() => {
    if (downloading) return 'Download in progress...';
    if (report?.status === 'IN_PROGRESS')
      return 'Incomplete report. Run in progress.';
    if ((report?.totalFailed ?? 0) === 0) return 'No failures to download.';
    return 'Download the report as xlsx.';
  })();

  const isDownloadDisabled =
    downloading || report?.status === 'IN_PROGRESS' || !report?.totalFailed;

  const downloadReport = async (reportToDownload: ConsistencyReportDetail) => {
    const onError = (err: unknown) =>
      notification.error({
        message: 'Could not download the report!',
        description: String(err),
      });
    const onSuccess = (desc: string) =>
      notification.success({
        message: 'Consistency report downloaded!',
        description: desc,
      });

    setDownloading(true);
    setError(null);

    try {
      const workbook = utils.book_new();

      // Summary sheet
      const summaryRows = reportToDownload.ruleResults.map((rule) => ({
        customerDefinedId: rule.customerDefinedId,
        'Error Message': rule.errorMessage ?? '-',
        'Source Schema Filter': rule.sourceSchemaFilterName,
        'Source Attribute': rule.sourceAttribute,
        Operator: RuleOperatorLabel[rule.operator],
        'Target Schema Filter': rule.targetSchemaFilterName,
        'Target Attribute': rule.targetAttribute,
        Severity: rule.severity,
        'Total Valid': rule.totalValid,
        'Total Failed': rule.totalFailed,
        'Success Rate': `${calculateSuccessRate(
          rule.totalValid,
          rule.totalFailed
        )}%`,
      }));

      const summarySheet = utils.json_to_sheet(summaryRows);
      utils.book_append_sheet(workbook, summarySheet, 'Summary');

      // Fetch failed instances for all rules in parallel
      const auth = { token, tenant, cluster, expiry };
      const failedRuleLabels: string[] = [];
      const failedByRule = await Promise.all(
        reportToDownload.ruleResults
          .filter((r: ConsistencyRuleRunResult) => r.totalFailed > 0)
          .map(async (rule: ConsistencyRuleRunResult) => {
            try {
              const instances = await fetchConsistencyFailedInstances(
                rule.runStatusId,
                auth,
                login
              );
              return {
                customerDefinedId: rule.customerDefinedId,
                errorMessage: rule.errorMessage ?? '-',
                severity: rule.severity,
                instances,
              };
            } catch {
              failedRuleLabels.push(rule.errorMessage ?? rule.ruleName);
              return {
                customerDefinedId: rule.customerDefinedId,
                errorMessage: rule.errorMessage ?? '-',
                severity: rule.severity,
                instances: [],
              };
            }
          })
      );

      if (failedRuleLabels.length > 0) {
        setError(
          `Could not fetch failed instances for: ${failedRuleLabels.join(', ')}`
        );
      }

      const date = new Date(reportToDownload.runAt).toISOString().slice(0, 10);

      const failedRows = failedByRule.flatMap(
        ({ customerDefinedId, errorMessage, severity, instances }) =>
          flattenJSON(instances).map((flat) => ({
            date,
            customerDefinedId,
            'Error Message': errorMessage,
            Severity: severity,
            ...flat,
          }))
      );

      if (failedRows.length > 0) {
        if (failedRows.length < BATCH_SIZE) {
          utils.book_append_sheet(
            workbook,
            utils.json_to_sheet(failedRows),
            'Failed Instances'
          );
        } else {
          for (let i = 0; i < failedRows.length; i += BATCH_SIZE) {
            const batch = failedRows.slice(i, i + BATCH_SIZE);
            const part = Math.floor(i / BATCH_SIZE) + 1;
            utils.book_append_sheet(
              workbook,
              utils.json_to_sheet(batch),
              `Failed Instances Part ${part}`
            );
          }
        }
      }

      const dateTime = new Date(reportToDownload.runAt).toLocaleDateString(
        undefined,
        { hour: '2-digit', minute: '2-digit' }
      );

      const filename = `Consistency report-${reportToDownload.collectionName}-${dateTime}.xlsx`;
      writeFile(workbook, filename);
      onSuccess(
        `Consistency report for collection ${reportToDownload.collectionName} was successfully downloaded`
      );
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : String(err);
      setError(message);
      onError(err);
    } finally {
      setDownloading(false);
    }
  };

  const handleDownload = () => {
    if (!report) return Promise.resolve();
    return downloadReport(report);
  };

  return {
    handleDownload,
    isDownloadDisabled,
    downloadTooltip,
    downloading,
    error,
  };
};
